import React, { useState, useEffect } from 'react';
import { Activity, Users, Phone, AlertTriangle, CheckCircle, XCircle, RefreshCw, DollarSign, LogOut, Wifi, ToggleLeft, ToggleRight } from 'lucide-react';
import { timeAgo, formatDate } from '../utils';
import AdminDiagnostics from './AdminDiagnostics';
import AdminCosts from './AdminCosts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ADMIN_SESSION_KEY = 'aizaan_admin_session';

export default function Admin() {
  const [adminKey, setAdminKey] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [providersData, setProvidersData] = useState(null);
  const [providerSaving, setProviderSaving] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Check session on mount
  useEffect(() => {
    // Check cia_admin_token from new admin login page first
    const adminToken = localStorage.getItem('cia_admin_token');
    const stored = sessionStorage.getItem(ADMIN_SESSION_KEY);
    if (adminToken || stored) {
      setIsLoggedIn(true);
      fetchData(stored);
      fetchProviders(stored);
    }
  }, []);

  const handleLogin = async () => {
    if (!adminKey.trim()) {
      setLoginError('Please enter the admin key');
      return;
    }
    setLoginLoading(true);
    setLoginError('');

    try {
      // Verify key by hitting a protected endpoint
      const res = await fetch(`${API_BASE}/admin/health`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      const result = await res.json();

      if (result.error === 'Unauthorized') {
        setLoginError('Invalid admin key');
        setLoginLoading(false);
        return;
      }

      // Valid — store in sessionStorage (clears when browser closes)
      sessionStorage.setItem(ADMIN_SESSION_KEY, adminKey);
      setIsLoggedIn(true);
      setHealth(result);
      await fetchData(adminKey);
      await fetchProviders(adminKey);
    } catch (e) {
      setLoginError('Connection failed');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setIsLoggedIn(false);
    setAdminKey('');
    setData(null);
    setHealth(null);
    setProvidersData(null);
  };

  const getHeaders = () => {
    const key = sessionStorage.getItem(ADMIN_SESSION_KEY);
    return { 'X-Admin-Key': key || '' };
  };

  const fetchData = async (key = null) => {
    setLoading(true);
    const adminToken = localStorage.getItem('cia_admin_token');
    const headers = adminToken 
      ? { 'Authorization': `Bearer ${adminToken}` }
      : { 'X-Admin-Key': key || sessionStorage.getItem(ADMIN_SESSION_KEY) || '' };
    try {
      const [dashRes, healthRes] = await Promise.all([
        fetch(`${API_BASE}/admin/dashboard`, { headers }),
        fetch(`${API_BASE}/admin/health`, { headers }),
      ]);
      setData(await dashRes.json());
      setHealth(await healthRes.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async (key = null) => {
    const adminToken = localStorage.getItem('cia_admin_token');
    const headers = adminToken 
      ? { 'Authorization': `Bearer ${adminToken}` }
      : { 'X-Admin-Key': key || sessionStorage.getItem(ADMIN_SESSION_KEY) || '' };
    try {
      const res = await fetch(`${API_BASE}/admin/providers`, { headers });
      const result = await res.json();
      setProvidersData(result);
    } catch (e) {
      console.error('Fetch providers error:', e);
    }
  };

  const toggleProvider = async (providerName, currentlyEnabled) => {
    setProviderSaving(providerName);
    try {
      const res = await fetch(`${API_BASE}/admin/providers/${providerName}`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: !currentlyEnabled }),
      });
      const result = await res.json();
      if (result.warning) {
        alert(result.warning);
      }
      await fetchProviders();
    } catch (e) {
      console.error('Toggle provider error:', e);
    }
    setProviderSaving('');
  };

  const updateRoutingRule = async (ruleId, providerName) => {
    setProviderSaving(ruleId);
    try {
      await fetch(`${API_BASE}/admin/routing/${ruleId}`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider_name: providerName }),
      });
      await fetchProviders();
    } catch (e) {
      console.error('Update routing error:', e);
    }
    setProviderSaving('');
  };

  const testProvider = async (providerName) => {
    setProviderSaving(`test-${providerName}`);
    try {
      const res = await fetch(`${API_BASE}/admin/providers/test/${providerName}`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const result = await res.json();
      if (result.success) {
        alert(`${providerName} is connected and working.\n${result.balance ? `Balance: ${result.balance} ${result.currency}` : result.status || ''}`);
      } else {
        alert(`${providerName} test failed: ${result.message || result.error || 'Unknown error'}`);
      }
    } catch (e) {
      alert(`Test failed: ${e.message}`);
    }
    setProviderSaving('');
  };

  const reprocessCall = async (callId) => {
    if (!window.confirm('Reprocess this call through full pipeline?')) return;
    const res = await fetch(`${API_BASE}/admin/calls/${callId}/reprocess`, { method: 'POST', headers: getHeaders() });
    const result = await res.json();
    alert(result.success ? 'Pipeline complete' : `Error: ${result.error}`);
  };

  const suspendCustomer = async (customerId) => {
    if (!window.confirm('Suspend this customer?')) return;
    await fetch(`${API_BASE}/admin/customers/${customerId}/suspend`, { method: 'POST', headers: getHeaders() });
    fetchData();
  };

  // ── Login screen ─────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 380,
        }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              width: 48, height: 48,
              background: 'var(--blue)',
              borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <Activity size={22} color="white" />
            </div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
              Aizaan Admin
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Internal control panel
            </p>
          </div>

          {loginError && (
            <div style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid #ef4444',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              color: '#ef4444',
              fontSize: 13,
            }}>
              {loginError}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Admin Key
            </label>
            <input
              type="password"
              placeholder="Enter admin key"
              value={adminKey}
              onChange={e => setAdminKey(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: 13,
              }}
            />
          </div>

          <button
            onClick={handleLogin}
            disabled={loginLoading}
            style={{
              width: '100%',
              padding: '11px',
              background: 'var(--blue)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {loginLoading ? 'Verifying...' : 'Sign In'}
          </button>
        </div>
      </div>
    );
  }

  // ── Admin dashboard ──────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-primary)' }}>
      <div className="spinner" />
    </div>
  );

  const summary = data?.summary || {};
  const calls = data?.calls || {};
  const customers = data?.customers || [];
  const errors = data?.recent_errors || [];

  const ServiceBadge = ({ name, info }) => (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: 8,
      padding: '12px 16px',
      background: 'var(--bg-primary)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {info?.status === 'ok'
          ? <CheckCircle size={16} color="var(--green)" />
          : <XCircle size={16} color="var(--red)" />
        }
        <span style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </div>
      <span style={{ fontSize: 12, color: info?.status === 'ok' ? 'var(--green)' : 'var(--red)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
        {info?.message || '—'}
      </span>
    </div>
  );

  return (
    <div className="page-wrap" style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ width: 36, height: 36, background: 'var(--blue)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Activity size={18} color="white" />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Aizaan Admin</h1>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Internal control panel</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: health?.overall === 'ok' ? 'var(--green)' : 'var(--red)', flexShrink: 0 }} />
          <span className="desktop-only" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            System {health?.overall === 'ok' ? 'healthy' : 'degraded'}
          </span>
          <button className="btn btn-ghost" onClick={() => fetchData()} style={{ fontSize: 12, padding: '6px 12px' }}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-scroll-bar" style={{ marginBottom: 24 }}>
        {['overview', 'customers', 'providers', 'errors', 'health', 'diagnostics', 'costs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 16px', background: 'none', border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--blue)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              cursor: 'pointer', textTransform: 'capitalize', marginBottom: -1,
              flexShrink: 0, whiteSpace: 'nowrap',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            {[
              { icon: DollarSign, color: 'var(--green)', value: `$${summary.mrr || 0}`, label: 'Monthly Recurring Revenue' },
              { icon: Users, color: 'var(--blue)', value: summary.active_customers || 0, label: 'Active Customers' },
              { icon: Phone, color: 'var(--cyan)', value: calls.this_month || 0, label: 'Calls This Month' },
              { icon: AlertTriangle, color: 'var(--orange)', value: calls.failed_transcriptions || 0, label: 'Failed Transcriptions' },
            ].map((stat, i) => (
              <div key={i} className="stat-card" style={{ borderLeft: `3px solid ${stat.color}` }}>
                <stat.icon size={18} color={stat.color} />
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="two-col-grid" style={{ marginBottom: 24 }}>
            <div className="card">
              <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Customers by Plan</h2>
              {['starter', 'growth', 'professional'].map(plan => {
                const count = summary.plan_breakdown?.[plan] || 0;
                const price = { starter: 49, growth: 99, professional: 199 }[plan];
                return (
                  <div key={plan} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: plan === 'professional' ? 'var(--green)' : plan === 'growth' ? 'var(--cyan)' : 'var(--blue)', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{plan}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>${price}/mo</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{count} customers</span>
                      <span style={{ fontSize: 13, color: 'var(--green)' }}>${count * price}/mo</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700 }}>
                <span>Total MRR</span>
                <span style={{ color: 'var(--green)' }}>${summary.mrr || 0}/mo</span>
              </div>
            </div>

            <div className="card">
              <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Call Volume</h2>
              {[
                { label: 'Today', value: calls.today || 0 },
                { label: 'This Week', value: calls.this_week || 0 },
                { label: 'This Month', value: calls.this_month || 0 },
                { label: 'Total All Time', value: calls.total || 0 },
                { label: 'Failed Transcriptions', value: calls.failed_transcriptions || 0, color: 'var(--red)' },
                { label: 'Failed Classifications', value: calls.failed_classifications || 0, color: 'var(--orange)' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: item.color || 'var(--text-primary)' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {summary.trials_ending_soon?.length > 0 && (
            <div className="card" style={{ borderLeft: '3px solid var(--orange)' }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--orange)' }}>
                ⚠️ Trials Ending Soon
              </h2>
              {summary.trials_ending_soon.map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name} — {t.email}</span>
                  <span style={{ color: 'var(--orange)', fontWeight: 600, flexShrink: 0 }}>{t.days_left} days left</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CUSTOMERS TAB */}
      {activeTab === 'customers' && (
        <div className="card table-wrap" style={{ padding: 0 }}>
          <table className="resp-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Plan</th>
                <th className="desktop-only">Virtual Number</th>
                <th>Calls (Month)</th>
                <th className="desktop-only">Total Calls</th>
                <th className="desktop-only">Joined</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id}>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.business_name}</div>
                  </td>
                  <td>
                    <span className={`badge ${c.plan === 'professional' ? 'badge-green' : c.plan === 'growth' ? 'badge-cyan' : 'badge-blue'}`}>
                      {c.plan}
                    </span>
                  </td>
                  <td className="desktop-only" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{c.virtual_number || '—'}</td>
                  <td style={{ fontWeight: 600, textAlign: 'center' }}>{c.calls_this_month}</td>
                  <td className="desktop-only" style={{ textAlign: 'center' }}>{c.total_calls}</td>
                  <td className="desktop-only" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{timeAgo(c.created_at)}</td>
                  <td>
                    <span className={`badge ${c.is_active ? 'badge-green' : 'badge-red'}`}>
                      {c.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => suspendCustomer(c.id)} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }}>
                      {c.is_active ? 'Suspend' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PROVIDERS TAB */}
      {activeTab === 'providers' && (
        <div>
          {!providersData ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading providers...</div>
          ) : (
            <>
              {/* Provider cards */}
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: 'var(--text-primary)' }}>
                  Telecom Providers
                </h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Enable a provider before routing any traffic to it. Configured = credentials are set in Railway. Enabled = available for routing.
                </p>
                {(providersData.providers || []).map(p => (
                  <div key={p.name} style={{
                    background: 'var(--bg-card, var(--bg-secondary))',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '16px 20px',
                    marginBottom: 10,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 16,
                  }}>
                    <Wifi size={18} color={p.is_enabled && p.is_configured ? 'var(--green)' : 'var(--text-muted)'} style={{ flexShrink: 0 }} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{p.display_name}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                          background: p.is_configured ? 'rgba(0,200,150,0.1)' : 'rgba(239,68,68,0.1)',
                          color: p.is_configured ? 'var(--green)' : '#ef4444',
                        }}>
                          {p.is_configured ? 'Configured' : 'Not Configured'}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {(p.supported_regions || []).join(', ')} · {(p.capabilities || []).join(', ')}
                      </div>
                      {p.notes && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>{p.notes}</div>
                      )}
                    </div>

                    <button
                      onClick={() => testProvider(p.name)}
                      disabled={!p.is_configured || providerSaving === `test-${p.name}`}
                      className="btn btn-ghost"
                      style={{ fontSize: 11, padding: '6px 12px', opacity: p.is_configured ? 1 : 0.4, flexShrink: 0 }}
                    >
                      {providerSaving === `test-${p.name}` ? 'Testing...' : 'Test'}
                    </button>

                    <button
                      onClick={() => toggleProvider(p.name, p.is_enabled)}
                      disabled={!p.is_configured || providerSaving === p.name}
                      style={{
                        background: 'none', border: 'none', cursor: p.is_configured ? 'pointer' : 'not-allowed',
                        color: p.is_enabled ? 'var(--green)' : 'var(--text-muted)',
                        opacity: p.is_configured ? 1 : 0.4, flexShrink: 0,
                      }}
                    >
                      {p.is_enabled ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
                    </button>
                  </div>
                ))}
              </div>

              {/* Routing table */}
              <div>
                <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: 'var(--text-primary)' }}>
                  Routing Rules
                </h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Which provider handles each country and call direction. Changes apply immediately to new calls/messages.
                </p>
                <div className="card table-wrap" style={{ padding: 0 }}>
                  <table className="resp-table">
                    <thead>
                      <tr>
                        <th>Country</th>
                        <th className="desktop-only">Direction</th>
                        <th>Provider</th>
                        <th>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(providersData.routing || []).map(rule => (
                        <tr key={rule.id}>
                          <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{rule.country_code}</td>
                          <td className="desktop-only" style={{ textTransform: 'capitalize', fontSize: 13 }}>{rule.direction}</td>
                          <td>
                            <select
                              value={rule.provider_name}
                              onChange={e => updateRoutingRule(rule.id, e.target.value)}
                              disabled={providerSaving === rule.id}
                              style={{
                                padding: '5px 10px', borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)', fontSize: 12,
                                maxWidth: '100%',
                              }}
                            >
                              {(providersData.providers || []).map(p => (
                                <option key={p.name} value={p.name} disabled={!p.is_configured}>
                                  {p.display_name}{!p.is_configured ? ' (not configured)' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <span className={`badge ${rule.is_active ? 'badge-green' : 'badge-red'}`}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ERRORS TAB */}
      {activeTab === 'errors' && (
        <div>
          {errors.length === 0 ? (
            <div className="empty-state">
              <CheckCircle size={32} color="var(--green)" />
              <p>No errors — all systems running clean</p>
            </div>
          ) : (
            <div className="card table-wrap" style={{ padding: 0 }}>
              <table className="resp-table">
                <thead>
                  <tr>
                    <th>Call ID</th>
                    <th>Error</th>
                    <th className="desktop-only">Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((err, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{err.call_id?.slice(0, 8)}...</td>
                      <td style={{ fontSize: 12, color: 'var(--red)', maxWidth: 400 }}>{err.error}</td>
                      <td className="desktop-only" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{timeAgo(err.created_at)}</td>
                      <td>
                        <button onClick={() => reprocessCall(err.call_id)} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }}>
                          Reprocess
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* HEALTH TAB */}
      {activeTab === 'health' && (
        <div style={{ width: '100%', maxWidth: 600 }}>
          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>System Status</h2>
            <ServiceBadge name="Supabase Database" info={health?.services?.supabase} />
            <ServiceBadge name="Twilio Telephony" info={health?.services?.twilio} />
            <ServiceBadge name="OpenAI Whisper" info={health?.services?.openai} />
            <ServiceBadge name="Anthropic Claude" info={health?.services?.anthropic} />
            <ServiceBadge name="WhatsApp / SMS" info={health?.services?.whatsapp} />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Last checked: {health?.checked_at ? formatDate(health.checked_at) : '—'}
          </p>
        </div>
      )}

      {/* DIAGNOSTICS TAB */}
      {activeTab === 'diagnostics' && (
        <AdminDiagnostics getHeaders={getHeaders} apiBase={API_BASE} />
      )}

      {/* COSTS TAB */}
      {activeTab === 'costs' && (
        <AdminCosts getHeaders={getHeaders} apiBase={API_BASE} />
      )}
    </div>
  );
}
